import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ReviewCard from '@/components/base/ReviewCard';
import { reviews } from '@/mocks/reviews';
import { useState } from 'react';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://chinahangnuo.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://chinahangnuo.com/about' },
      ],
    },
    {
      '@type': 'AboutPage',
      name: '关于韩漫画网',
      url: 'http://chinahangnuo.com/about',
      description: '韩漫画网是专业的韩国漫画免费在线阅读平台，提供海量优质韩漫资源，致力于为华语漫画爱好者提供最佳的阅读体验。',
      isPartOf: { '@type': 'WebSite', name: '韩漫画网', url: 'http://chinahangnuo.com/' },
    },
    {
      '@type': 'Organization',
      name: '韩漫画网',
      url: 'http://chinahangnuo.com/',
      description: '韩漫画网提供免费漫画在线阅读和韩国漫画网站大全，覆盖热血、恋爱、奇幻、悬疑等多种韩国漫画类型。',
      foundingDate: '2023',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'hello@chinahangnuo.com',
        availableLanguage: ['Chinese'],
      },
    },
  ],
};

export default function About() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', message: '' });
      return;
    }
    setContactLoading(true);
    setContactError('');
    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const res = await fetch('http://chinahangnuo.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setContactSubmitted(true);
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后再试';
        setContactError(msg);
      }
    } catch {
      setContactError('网络错误，请稍后重试');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫画网 - 免费韩国漫画在线阅读平台"
        description="韩漫画网是专业的韩国漫画免费在线阅读平台，成立于2023年，致力于为华语漫画爱好者提供最全面、最优质的韩国漫画资源。"
        keywords="关于韩漫画网,韩漫画网介绍,韩漫画网联系,韩漫画网品牌"
        canonicalUrl="http://chinahangnuo.com/about"
        schema={aboutSchema}
      />

      <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

      <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="text-xs font-medium text-accent-600 tracking-wider mb-3">关于我们</p>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-6">
            韩漫画网
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-4">
            韩漫画网成立于2023年，是专业的<strong>韩国漫画免费在线阅读平台</strong>。我们致力于为华语漫画爱好者提供最全面、最优质的韩国漫画资源，让每一位读者都能在这里找到心仪的作品。
          </p>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
            从热血激燃的战斗番到甜蜜浪漫的恋爱故事，从烧脑悬疑的推理作品到轻松搞笑的日常喜剧，我们精心收录了超过12,000部韩国漫画作品，覆盖几乎所有热门类型。每一部漫画都经过严格筛选，确保高清画质和优质的阅读体验。
          </p>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
            我们的团队由一群热爱漫画的年轻人组成。我们深知漫画爱好者的需求——快速更新、高清画质、流畅阅读。正因如此，我们每天都在努力，为用户提供最好的韩漫阅读服务。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <span className="text-xs font-medium text-accent-600 tracking-wider">口碑见证</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              读者与合作伙伴的评价
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              来自用户的真实反馈，是我们前进的最大动力
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="flex flex-col lg:flex-row">
        <div className="relative lg:w-1/2 h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20illustration%2C%20friendly%20characters%20waving%20and%20greeting%2C%20warm%20and%20welcoming%20atmosphere%2C%20soft%20golden%20and%20cream%20color%20palette%2C%20clean%20composition%2C%20anime%20manhwa%20art%20style%2C%20professional%20yet%20friendly%20tone&width=960&height=600&seq=about-contact-visual&orientation=landscape"
            alt="联系我们 - 韩漫画网"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/25 to-transparent"></div>
          <div className="absolute bottom-5 left-5 md:bottom-8 md:left-8">
            <p className="text-xs text-background-50/70 mb-1 tracking-wider">商务合作</p>
            <h3 className="text-lg md:text-2xl font-bold font-heading text-background-50 leading-tight">
              期待与您
              <br />
              携手合作
            </h3>
          </div>
        </div>

        <div className="lg:w-1/2 bg-background-100 flex items-center px-6 md:px-12 py-10 md:py-14">
          <div className="max-w-md w-full">
            <h3 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-2">
              联系我们
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-8 leading-relaxed">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。
            </p>

            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                  <p className="text-xs text-foreground-500">hello@chinahangnuo.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-wechat-line"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                  <p className="text-xs text-foreground-500">韩漫画网</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-global-line"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">官方网站</p>
                  <p className="text-xs text-foreground-500">hanmanhua.com</p>
                </div>
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleContactSubmit}>
              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <div>
                <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">您的称呼</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors duration-200"
                  placeholder="请输入您的称呼"
                  required
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors duration-200"
                  placeholder="请输入您的邮箱"
                  required
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full bg-background-50 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors duration-200 resize-none"
                  rows={4}
                  placeholder="请输入您的留言内容（最多500字）"
                  maxLength={500}
                  required
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={contactLoading}
                className="w-full bg-primary-500 text-background-50 py-3 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60"
              >
                {contactLoading ? '发送中...' : contactSubmitted ? '发送成功 ✓' : '发送留言'}
              </button>
              {contactSubmitted && !contactLoading && (
                <p className="text-xs text-accent-600 text-center">感谢您的留言，我们会尽快回复！</p>
              )}
              {contactError && (
                <p className="text-xs text-red-500 text-center">{contactError}</p>
              )}
            </form>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-10">
            常见问题
          </h2>
          <div className="space-y-4">
            <details className="bg-background-50 rounded-lg border border-background-200/60 group">
              <summary className="px-6 py-4 text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                韩漫画网是如何运营的？
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
              </summary>
              <p className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                韩漫画网通过广告收入和合作伙伴支持来维持运营，为读者提供<strong>完全免费</strong>的韩国漫画在线阅读服务。我们致力于在免费的前提下，持续提供高质量的漫画内容和阅读体验。
              </p>
            </details>
            <details className="bg-background-50 rounded-lg border border-background-200/60 group">
              <summary className="px-6 py-4 text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                如何与韩漫画网进行商务合作？
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
              </summary>
              <p className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                我们欢迎各类商务合作，包括广告投放、内容合作、品牌联动等。请通过上方<strong>联系表单</strong>或发送邮件至 hello@chinahangnuo.com，我们的商务团队会在1-3个工作日内与您取得联系。
              </p>
            </details>
            <details className="bg-background-50 rounded-lg border border-background-200/60 group">
              <summary className="px-6 py-4 text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                韩漫画网的内容来源是什么？
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
              </summary>
              <p className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                韩漫画网收录的韩国漫画作品均来自<strong>公开的正版授权渠道</strong>和用户推荐。我们尊重知识产权，如有版权问题请联系我们，我们将及时处理。
              </p>
            </details>
          </div>
        </div>
      </section>
    </>
  );
}